import styled from "styled-components";
import { FC, MouseEvent, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import Text from "@/components/Text";
import TableButton from "./TableButton";
import ShoppingListModal from "./ShoppingListModal";
import { ProductionSceneShoppingItemResource } from "@/types/resources";
import { Bullet, BulletKey, BulletValue } from "./PropsTableRow";

import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

const SceneNumber = styled(Text)`
  background: ${({ theme }) => theme.colors.lightGray};
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 6px;
`;

const TitleHolder = styled.div`
  display: flex;
  align-items: flex-start;
`;

interface Props {
  shoppingItem: ProductionSceneShoppingItemResource;
  sceneIndex: number;
  scenes: number[];
}

const ShoppingListTableRow: FC<Props> = ({
  shoppingItem,
  sceneIndex,
  scenes,
}) => {
  const [showModal, setShowModal] = useState(false);

  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this shopping item?"
      );

      if (confirm) {
        await api.delete<never, { id: number }>(
          `/v1/production/scenes/shopping-items/${shoppingItem.id}`
        );
        alerts.success("Shopping item successfully deleted");

        queryClient.invalidateQueries(["production_projects", { projectId }]);
      }
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        <TitleHolder>
          <SceneNumber $type="extraSmall">{sceneIndex}</SceneNumber>
          {shoppingItem.title}
        </TitleHolder>
        <div>
          {shoppingItem.address && (
            <Bullet>
              <BulletKey $type="extraSmall">Location:</BulletKey>
              <BulletValue $type="extraSmall" $color="gray">
                {shoppingItem.address}
              </BulletValue>
            </Bullet>
          )}
          {shoppingItem.time && (
            <Bullet>
              <BulletKey $type="extraSmall">Date & time:</BulletKey>
              <BulletValue $type="extraSmall" $color="gray">
                {DateTime.fromISO(shoppingItem.time).toFormat(
                  "dd/LL/yyyy HH:mm"
                )}
              </BulletValue>
            </Bullet>
          )}
        </div>
      </Td>
      <Td>{shoppingItem.content}</Td>
      <Td>
        {shoppingItem.owner ? (
          <>{shoppingItem.owner}</>
        ) : (
          <TableButton text="Add name" onClick={() => setShowModal(true)} />
        )}
      </Td>
      <Td>
        {shoppingItem.note ? (
          <>{shoppingItem.note}</>
        ) : (
          <TableButton text="Add note" onClick={() => setShowModal(true)} />
        )}
      </Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowModal(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
      <AnimatePresence>
        {!!showModal && (
          <ShoppingListModal
            shoppingItem={shoppingItem}
            scenes={scenes}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default ShoppingListTableRow;
